import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown, BookOpen, Loader2, AlertCircle } from "lucide-react";
import { fetchTenantLibrary } from "../lib/tenantApi";
import TenantShell from "../components/TenantShell";

const TYPE_BADGE = {
  Domain: "bg-sky-50 text-sky-700 border-sky-200",
  Tech: "bg-violet-50 text-violet-700 border-violet-200",
  Soft: "bg-amber-50 text-amber-700 border-amber-200",
};

function SkillCard({ skill, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const anchors = skill.behavioral_anchors || {};
  const anchorEntries = ["1", "2", "3", "4", "5"]
    .map((k) => [k, anchors[k]])
    .filter(([, v]) => v);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-sm transition-shadow">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-5 text-left flex items-start justify-between gap-3"
        data-testid={`skill-card-${skill.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="font-mono text-[0.65rem] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{skill.id}</span>
            <span
              className={`text-[0.65rem] font-medium px-2 py-0.5 rounded-full border ${
                TYPE_BADGE[skill.type] || "bg-slate-50 text-slate-600 border-slate-200"
              }`}
            >
              {skill.type}
            </span>
            {skill.weight ? (
              <span className="text-[0.65rem] text-slate-500">Ağırlık {Math.round(skill.weight * 100)}%</span>
            ) : null}
          </div>
          <div className="font-display font-semibold text-slate-900">{skill.name}</div>
          {skill.description ? (
            <p className="text-xs text-slate-500 mt-1.5 leading-snug line-clamp-2">{skill.description}</p>
          ) : null}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 mt-1 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50">
          <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-3 font-semibold">
            Davranışsal Çıpalar (1–5)
          </div>
          <div className="space-y-2.5">
            {anchorEntries.map(([lvl, txt]) => (
              <div key={lvl} className="flex gap-3 text-sm" data-testid={`skill-${skill.id}-anchor-${lvl}`}>
                <div
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{
                    background: ["#94a3b8", "#64748b", "#0ea5e9", "#0284c7", "#075985"][Number(lvl) - 1],
                  }}
                >
                  {lvl}
                </div>
                <p className="text-slate-700 leading-relaxed flex-1 pt-0.5">{txt}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompetencyLibrary() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCluster, setActiveCluster] = useState(null);
  const [filterType, setFilterType] = useState("all"); // all | Domain | Tech | Soft
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchTenantLibrary(slug)
      .then((d) => {
        setData(d);
        if (d.skill_clusters?.[0]) setActiveCluster(d.skill_clusters[0].id);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const activeClusterObj = useMemo(
    () => data?.skill_clusters?.find((c) => c.id === activeCluster),
    [data, activeCluster],
  );

  const filteredSkills = useMemo(() => {
    const list = activeClusterObj?.skills || [];
    return list.filter((s) => {
      if (filterType !== "all" && s.type !== filterType) return false;
      if (search && !(`${s.name} ${s.description || ""}`.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [activeClusterObj, filterType, search]);

  if (loading) {
    return (
      <TenantShell active="library">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </TenantShell>
    );
  }

  if (!data) {
    return (
      <TenantShell active="library">
        <div className="text-center py-20" data-testid="library-empty">
          <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Kütüphane bulunamadı</h3>
          <p className="text-sm text-slate-500 mt-1">Bu tenant için yetkinlik kütüphanesi henüz tanımlı değil.</p>
        </div>
      </TenantShell>
    );
  }

  const totalSkills = data.skill_clusters?.reduce((sum, c) => sum + (c.skills?.length || 0), 0) || 0;

  return (
    <TenantShell active="library">
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
          <BookOpen className="w-3.5 h-3.5" />
          Sektörel Yetkinlik Kütüphanesi
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2">
          {data.meta?.edition_name || "Yetkinlik Kütüphanesi"}
        </h1>
        <p className="text-slate-600 max-w-2xl">
          {totalSkills} yetkinlik · {data.skill_clusters?.length || 0} kategori · 5 seviyeli davranışsal çıpalar.
          Performans değerlendirmelerinden kariyer planlamasına kadar tüm yetenek kararlarına temel oluşturur.
        </p>
      </div>

      {/* Cluster tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="cluster-tabs">
        {data.skill_clusters?.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCluster(c.id)}
            data-testid={`cluster-tab-${c.category}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              activeCluster === c.id
                ? "text-white border-transparent shadow-sm"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            style={activeCluster === c.id ? { background: c.color || "#0ea5e9" } : {}}
          >
            {c.name} <span className="ml-1.5 opacity-60">({c.skills?.length || 0})</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="text"
          placeholder="Yetkinlik ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="skill-search"
          className="flex-1 px-4 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:border-sky-500"
        />
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {["all", "Domain", "Tech", "Soft"].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              data-testid={`filter-type-${t}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filterType === t ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t === "all" ? "Hepsi" : t}
            </button>
          ))}
        </div>
      </div>

      {/* Skill cards */}
      {filteredSkills.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500">Filtreye uyan yetkinlik bulunamadı.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="skill-grid">
          {filteredSkills.map((s) => (
            <SkillCard key={s.id} skill={s} />
          ))}
        </div>
      )}

      {/* KPI footer info */}
      {data.kpi_sets && data.kpi_sets.length > 0 ? (
        <div className="mt-12 bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-semibold text-slate-900 mb-1">KPI Setleri</h3>
          <p className="text-xs text-slate-500 mb-4">
            {data.kpi_sets.length} KPI tanımı · Eşik (yeşil/sarı), ağırlık ve prim cetveli ile.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.kpi_sets.slice(0, 9).map((kp) => (
              <div
                key={kp.id}
                className="border border-slate-200 rounded-lg p-3 text-xs"
                data-testid={`kpi-card-${kp.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[0.65rem] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {kp.id}
                  </span>
                  {kp.drives_commission ? (
                    <span className="text-[0.6rem] uppercase tracking-wider text-amber-600 font-semibold">
                      Prim
                    </span>
                  ) : null}
                </div>
                <div className="font-semibold text-slate-900 mb-1">{kp.name}</div>
                <div className="text-slate-500 leading-snug">{kp.desc}</div>
                {kp.thresholds ? (
                  <div className="mt-2 flex gap-2 text-[0.6rem]">
                    <span className="text-emerald-600">≥{kp.thresholds.green}{kp.unit}</span>
                    <span className="text-amber-600">{kp.thresholds.amber}{kp.unit}+</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </TenantShell>
  );
}
